/**
 * usePortfolioTransactions Hook
 * Hook for managing portfolio transactions
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, this hook only fetches and displays pre-calculated data.
 * All calculations are performed by backend Portfolio Service.
 */

import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchTransactions } from '../../../store/slices/portfolioSlice';
import { PortfolioTransaction } from '../../../types/portfolio';

export interface UsePortfolioTransactionsOptions {
  brokerAccountId?: string;
  transactionType?: 'buy' | 'sell';
  symbol?: string;
  exchange?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
  autoRefresh?: boolean;
}

export interface UsePortfolioTransactionsResult {
  transactions: PortfolioTransaction[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function usePortfolioTransactions(
  options: UsePortfolioTransactionsOptions = {}
): UsePortfolioTransactionsResult {
  const dispatch = useAppDispatch();
  const transactions = useAppSelector((state) => state.portfolio.transactions);
  const transactionsTotal = useAppSelector((state) => state.portfolio.transactionsTotal);
  const transactionsLoading = useAppSelector((state) => state.portfolio.transactionsLoading);
  const error = useAppSelector((state) => state.portfolio.error);

  const refresh = useCallback(async () => {
    try {
      await dispatch(fetchTransactions(options)).unwrap();
    } catch (err) {
      // Error handled by Redux slice
      // eslint-disable-next-line no-console
      console.error('Failed to refresh transactions:', err);
    }
  }, [dispatch, options]);

  useEffect(() => {
    if (options.autoRefresh !== false) {
      refresh();
    }
  }, [refresh, options.autoRefresh]);

  return {
    transactions,
    total: transactionsTotal,
    loading: transactionsLoading,
    error,
    refresh,
  };
}

